import { defineComponent as _defineComponent } from 'vue';
import { createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "tb-left-label"
};
const _hoisted_2 = {
    key: 3,
    class: "tb-suggest"
};
const _hoisted_3 = { class: "tb-suggest-caption" };
const _hoisted_4 = ["src"];
const _hoisted_5 = { class: "tb-suggest-caption" };
const _hoisted_6 = {
    key: 0,
    class: "tb-actions"
};
const _hoisted_7 = { class: "tb-toggle-row" };
const _hoisted_8 = { class: "tb-toggle-row" };
const _hoisted_9 = { class: "tb-cta-row" };
const _hoisted_10 = ["disabled"];
const _hoisted_11 = { class: "tb-cta-circle tb-cta-circle--other" };
const _hoisted_12 = ["src"];
const _hoisted_13 = { class: "tb-cta-label" };
const _hoisted_14 = ["disabled"];
const _hoisted_15 = ["src"];
const _hoisted_16 = { class: "tb-cta-label" };
const _hoisted_17 = {
    key: 0,
    class: "tb-suggest-skeleton tb-suggest-skeleton--cta"
};
const _hoisted_18 = { class: "tb-tx-drawer" };
import { ref, computed, onMounted, useTemplateRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAppState } from '@/composables/useAppState';
import { useAuth } from '@/composables/useAuth';
import { useCustomer } from '@/composables/useCustomer';
import { useBanking } from '@/composables/useBanking';
import { useTransactions } from '@/composables/useTransactions';
import { useSpendingAnalysis, getTransactionClassificationTree } from '@/composables/useSpendingAnalysis';
import { selectedAccount } from './elements/spendingAnalysisFilterState';
import HeaderBlock from './elements/HeaderBlock.vue';
import MGButton from './elements/MGButton.vue';
import MGToggle from './elements/MGToggle.vue';
import PaginationDots from './elements/PaginationDots.vue';
import ChangeCategoryDrawer from './elements/ChangeCategoryDrawer.vue';
import TransactionGroupCard from './elements/TransactionGroupCard.vue';
import MGDialog from './elements/MGDialog.vue';
import SimpleDrawerPanel from './elements/SimpleDrawerPanel.vue';
import TransactionsList from './elements/TransactionsList.vue';
import { getCategoryIconUrl } from '@/utils/helpers';
const SWIPE_THRESHOLD = 50;
export default /*@__PURE__*/ _defineComponent({
    __name: 'TransactionBulkPage',
    setup(__props) {
        const state = useAppState();
        const router = useRouter();
        const route = useRoute();
        useAuth();
        const { getCustomer, getCustomerTags } = useCustomer();
        const { getBankingInfo } = useBanking();
        const transactionsApi = useTransactions();
        useSpendingAnalysis();
        const drawer = useTemplateRef('drawer');
        const queue = ref([]);
        const index = ref(0);
        const suggestion = ref(null);
        const suggestionLoading = ref(false);
        const fetchingMerchant = ref(null);
        const applying = ref(false);
        const classificationTree = ref(null);
        const alwaysApply = ref(false);
        const excludeFromAnalysis = ref(false);
        const alwaysApplyExcludeInclude = ref(false);
        const excludeDialogVisible = ref(false);
        const revertingExclude = ref(false);
        const appliedCount = ref(0);
        const availableTags = ref(null);
        const swipeStart = ref(null);
        const current = computed(() => queue.value[index.value] ?? null);
        const currentMerchantName = computed(() => current.value?.key ?? '');
        const txDrawerOpen = ref(false);
        const currentCount = computed(() => current.value?.value.length ?? 0);
        const countLabel = computed(() => `${currentCount.value} transaction${currentCount.value === 1 ? '' : 's'}`);
        const markLabel = computed(() => currentCount.value === 1 ? 'transaction' : `${currentCount.value} transactions`);
        const viewLabel = computed(() => currentCount.value === 1 ? 'transaction' : `${currentCount.value} transactions`);
        const suggestionSub = computed(() => {
            const s = suggestion.value;
            if (!s)
                return '';
            const mg = s.mgClassification;
            const node = (typeof mg === 'object' && mg) ? mg : s;
            return node.mgSubClassification ?? node.fullName ?? '';
        });
        const isOtherSuggestion = computed(() => {
            const sub = suggestionSub.value.toLowerCase();
            return !suggestion.value || sub === 'uncategorised' || sub === 'uncategorized' || sub === 'other';
        });
        const suggestionDisplayLower = computed(() => (suggestionSub.value || 'other').toLowerCase());
        const suggestionDisplayUpper = computed(() => (suggestionSub.value || 'OTHER').toUpperCase());
        // Parent category for an "Other" suggestion — e.g. "Essentials" when the
        // suggested sub is "Essentials > Other". Empty when there's no suggestion at all.
        const suggestionParent = computed(() => {
            const s = suggestion.value;
            if (!s)
                return '';
            const mg = s.mgClassification;
            if (typeof mg === 'object' && mg)
                return mg.mgClassification ?? '';
            if (typeof mg === 'string')
                return mg;
            return '';
        });
        // Label for the green "Assign to …" button when the suggestion is Other/uncategorised.
        // Includes the parent so users can tell which "Other" they're assigning to.
        const otherAssignLabel = computed(() => {
            const parent = suggestionParent.value;
            return parent ? `${parent} (Other)` : 'OTHER';
        });
        const suggestionIconUrl = computed(() => {
            if (isOtherSuggestion.value)
                return '';
            const s = suggestion.value;
            const mg = s?.mgClassification;
            const node = (typeof mg === 'object' && mg) ? mg : s;
            return getCategoryIconUrl(node?.iconPath);
        });
        const suggestionAvatarStyle = computed(() => {
            const s = suggestion.value;
            const mg = s?.mgClassification;
            const node = (typeof mg === 'object' && mg) ? mg : s;
            const colour = node?.color;
            if (!colour)
                return {};
            return { backgroundColor: colour };
        });
        const groupedCurrentTransactions = computed(() => {
            const txs = current.value?.value ?? [];
            const buckets = new Map();
            for (const t of txs) {
                const key = t.timestampString ?? (t.timestamp ?? '').slice(0, 10);
                const arr = buckets.get(key) ?? [];
                arr.push(t);
                buckets.set(key, arr);
            }
            return Array.from(buckets.values());
        });
        const suggestionName = computed(() => {
            const s = suggestion.value;
            if (!s)
                return '';
            const mg = s.mgClassification;
            const node = (typeof mg === 'object' && mg) ? mg : s;
            const parent = (typeof mg === 'object' && mg) ? (mg.mgClassification ?? '') : (typeof mg === 'string' ? mg : '');
            const sub = node.mgSubClassification ?? '';
            if (parent && sub)
                return `${sub} (${parent})`;
            return sub || parent || node.fullName || 'this category';
        });
        function getAccount(t) {
            const id = t?.bankAccountID ?? t?.bankAccountId;
            return state.bankInfo?.accounts?.find((a) => a.id === id);
        }
        function getGroupAmount(txs) {
            return txs.reduce((sum, t) => sum + t.amount, 0);
        }
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        function formatTimestamp(ts) {
            if (!ts)
                return '';
            const d = new Date(ts);
            if (Number.isNaN(d.getTime()))
                return '';
            const yy = String(d.getFullYear()).slice(-2);
            return `${dayNames[d.getDay()]}, ${d.getDate()} ${monthNames[d.getMonth()]} ${yy}`;
        }
        function fetchSuggestion() {
            const merchant = currentMerchantName.value;
            if (!merchant) {
                suggestion.value = null;
                return;
            }
            if (fetchingMerchant.value === merchant)
                return;
            // Clear the previous merchant's suggestion so the loading skeleton shows
            // immediately on swipe instead of briefly displaying stale data (and so the
            // "Assign to …" CTA can't apply the old category to the new merchant).
            suggestion.value = null;
            fetchingMerchant.value = merchant;
            suggestionLoading.value = true;
            transactionsApi.getSuggestedClassification(merchant, (result) => {
                if (fetchingMerchant.value !== merchant)
                    return;
                if (result?.id && classificationTree.value) {
                    const found = findInTreeById(result.id, classificationTree.value);
                    suggestion.value = found ?? result;
                }
                else {
                    suggestion.value = result ?? null;
                }
                // Backend workaround: Income/Other is returned for unmatched merchants
                // regardless of direction. Swap to General/Other for spend transactions
                // (negative total); leave positive (income) transactions untouched.
                const resolved = suggestion.value;
                const mg = resolved?.mgClassification;
                const parentName = typeof mg === 'object' && mg ? mg.mgClassification : mg;
                const subName = resolved?.mgSubClassification
                    ?? (typeof mg === 'object' && mg ? mg.mgSubClassification : undefined);
                const isIncomeOther = parentName === 'Income' && subName === 'Other';
                const groupAmount = current.value ? getGroupAmount(current.value.value) : 0;
                if (isIncomeOther && groupAmount < 0 && classificationTree.value) {
                    const generalOther = findGeneralOther(classificationTree.value);
                    if (generalOther)
                        suggestion.value = generalOther;
                }
                suggestionLoading.value = false;
                fetchingMerchant.value = null;
            });
        }
        function onYes() {
            if (!suggestion.value || applying.value)
                return;
            apply({
                classification: suggestion.value,
                alwaysApply: alwaysApply.value,
                excludeFromAnalysis: excludeFromAnalysis.value,
                alwaysApplyExcludeInclude: alwaysApplyExcludeInclude.value,
            });
        }
        function onNo() {
            if (applying.value)
                return;
            drawer.value?.openDrawer();
        }
        function onPickedFromDrawer(payload) {
            if (!payload.classification) {
                drawer.value?.closeDrawer();
                return;
            }
            apply(payload);
        }
        function applyTagsToGroup(txs, tags, done) {
            if (!tags.length) {
                done();
                return;
            }
            const payload = tags.map(tag => ({ tag }));
            let remaining = txs.length;
            if (!remaining) {
                done();
                return;
            }
            for (const tx of txs) {
                transactionsApi.updateTransactionTags(tx.bankAccountID ?? tx.bankAccountId, tx.id, payload, () => {
                    remaining -= 1;
                    if (remaining === 0)
                        done();
                });
            }
        }
        function apply(payload) {
            if (applying.value)
                return;
            const group = current.value;
            if (!group)
                return;
            applying.value = true;
            const txs = group.value;
            transactionsApi.applyCategoryToTransactions(txs, payload, () => {
                applyTagsToGroup(txs, payload.tags ?? [], () => {
                    appliedCount.value += txs.length;
                    applying.value = false;
                    advance();
                });
            });
        }
        function advance() {
            drawer.value?.closeDrawer();
            alwaysApply.value = false;
            revertingExclude.value = true;
            excludeFromAnalysis.value = false;
            alwaysApplyExcludeInclude.value = false;
            excludeDialogVisible.value = false;
            if (queue.value.length <= 1) {
                if (appliedCount.value > 0) {
                    sessionStorage.setItem('saCategorisedToast', String(appliedCount.value));
                }
                router.replace('/SpendingAnalysis');
                return;
            }
            const removedIdx = index.value;
            queue.value = queue.value.filter((_, i) => i !== removedIdx);
            if (index.value >= queue.value.length)
                index.value = queue.value.length - 1;
        }
        function onSwipeStart(e) {
            const t = e.touches[0];
            if (!t) {
                swipeStart.value = null;
                return;
            }
            swipeStart.value = { x: t.clientX, y: t.clientY };
        }
        function onSwipeEnd(e) {
            const start = swipeStart.value;
            swipeStart.value = null;
            if (!start || !current.value || applying.value)
                return;
            const t = e.changedTouches[0];
            if (!t)
                return;
            const dx = t.clientX - start.x;
            const dy = t.clientY - start.y;
            if (Math.abs(dx) <= Math.abs(dy))
                return;
            if (Math.abs(dx) < SWIPE_THRESHOLD)
                return;
            if (dx < 0) {
                index.value = Math.min(index.value + 1, queue.value.length - 1);
            }
            else {
                index.value = Math.max(index.value - 1, 0);
            }
        }
        function onBack() {
            if (appliedCount.value > 0) {
                sessionStorage.setItem('saCategorisedToast', String(appliedCount.value));
            }
            router.replace('/SpendingAnalysis');
        }
        watch(current, () => {
            if (current.value)
                fetchSuggestion();
        });
        watch(excludeFromAnalysis, () => {
            if (revertingExclude.value) {
                revertingExclude.value = false;
                return;
            }
            alwaysApplyExcludeInclude.value = false;
            excludeDialogVisible.value = true;
        });
        function confirmAlwaysExcludeInclude(always) {
            alwaysApplyExcludeInclude.value = always;
            excludeDialogVisible.value = false;
        }
        function cancelExcludeChange() {
            revertingExclude.value = true;
            excludeFromAnalysis.value = !excludeFromAnalysis.value;
            excludeDialogVisible.value = false;
        }
        function groupByMerchant(txs) {
            const map = new Map();
            for (const t of txs) {
                const key = t.merchantName || 'Unknown';
                const arr = map.get(key) ?? [];
                arr.push(t);
                map.set(key, arr);
            }
            const groups = [];
            map.forEach((value, key) => groups.push({ key, value }));
            groups.sort((a, b) => new Date(b.value[0].timestamp).getTime() - new Date(a.value[0].timestamp).getTime());
            return groups;
        }
        function selectedAccountIdsForRequest() {
            const key = selectedAccount.value;
            const accounts = (state.bankInfo?.accounts || []).filter((a) => a.source === 0);
            if (!accounts.length || key === 'all')
                return undefined;
            const typeMap = { current: 'Current', credit: 'Credit', savings: 'Savings' };
            if (typeMap[key]) {
                const ids = accounts.filter((a) => a.accountType?.prettyName === typeMap[key]).map((a) => a.id);
                return ids.length ? ids : undefined;
            }
            const match = accounts.find((a) => a.id === key);
            return match ? [match.id] : undefined;
        }
        function rangeFromQuery() {
            const f = route.query.from;
            const t = route.query.to;
            if (typeof f !== 'string' || typeof t !== 'string')
                return null;
            const fd = new Date(f);
            const td = new Date(t);
            if (Number.isNaN(fd.getTime()) || Number.isNaN(td.getTime()))
                return null;
            return { fromDate: fd, toDate: td };
        }
        function currentCycleRange() {
            // Always operates on the current pay cycle, matching the master page and
            // the `untaggedTransactionsCurrentCycleCount` indicator shown on Home/Activity.
            const now = new Date();
            const cycleDay = state.bankInfo?.spendingCycle?.day ?? 1;
            let endY = now.getUTCFullYear();
            let endM = now.getUTCMonth();
            if (now.getUTCDate() >= cycleDay) {
                endM += 1;
                if (endM > 11) {
                    endM = 0;
                    endY += 1;
                }
            }
            let startY = endY;
            let startM = endM - 1;
            if (startM < 0) {
                startM = 11;
                startY -= 1;
            }
            return {
                fromDate: new Date(Date.UTC(startY, startM, cycleDay)),
                toDate: new Date(Date.UTC(endY, endM, cycleDay)),
            };
        }
        function findUncategorisedId(tree) {
            const parents = Array.isArray(tree?.parents) ? tree.parents : [];
            for (const p of parents) {
                const subs = Array.isArray(p.subClassifications) ? p.subClassifications : [];
                for (const s of subs) {
                    const sub = s.mgSubClassification || s.mgClassification;
                    if (sub === 'Uncategorised' || sub === 'Uncategorized')
                        return s.id;
                }
            }
            return null;
        }
        // Backend workaround: when the suggested classification is "Income > Other" but
        // the transaction is a spend, swap to "General > Other". The suggestion endpoint
        // returns Income/Other regardless of direction when it has no real match.
        function findGeneralOther(tree) {
            const parents = Array.isArray(tree?.parents) ? tree.parents : [];
            const general = parents.find((p) => p.name === 'General');
            if (!general)
                return null;
            const subs = Array.isArray(general.subClassifications) ? general.subClassifications : [];
            return subs.find((s) => (s.mgSubClassification || s.mgClassification) === 'Other') ?? null;
        }
        function findInTreeById(id, tree) {
            const parents = Array.isArray(tree?.parents) ? tree.parents : [];
            for (const p of parents) {
                const subs = Array.isArray(p.subClassifications) ? p.subClassifications : [];
                for (const s of subs)
                    if (s.id === id)
                        return s;
            }
            return null;
        }
        function loadUncategorised() {
            let loaded = false;
            getTransactionClassificationTree((tree) => {
                classificationTree.value = tree;
                if (loaded)
                    return;
                loaded = true;
                const uncatId = findUncategorisedId(tree);
                if (!uncatId) {
                    console.warn('[TransactionBulkPage] could not find Uncategorised classification in tree', tree);
                    router.replace('/SpendingAnalysis');
                    return;
                }
                // Caller may pass `from`/`to` in the URL (e.g. SpendingAnalysisPage linking
                // to a historical month). Falls back to the current cycle otherwise.
                const { fromDate, toDate } = rangeFromQuery() ?? currentCycleRange();
                transactionsApi.getTransactions(uncatId, fromDate.toISOString(), toDate.toISOString(), () => {
                    const all = (state.transactions?.transactions) ?? [];
                    // getTransactions has no account filter — apply client-side using the
                    // same `selectedAccount` filter the user picked on SpendingAnalysisPage.
                    const accountIds = selectedAccountIdsForRequest();
                    const filtered = accountIds
                        ? all.filter(t => accountIds.includes(t.bankAccountID ?? t.bankAccountId))
                        : all;
                    const groups = groupByMerchant(filtered);
                    if (!groups.length) {
                        router.replace('/SpendingAnalysis');
                        return;
                    }
                    index.value = 0;
                    queue.value = groups;
                }, /* includeChildren */ false, /* refreshCache */ true);
            }, /* refreshCache */ true);
        }
        onMounted(() => {
            getCustomer(() => {
                getBankingInfo(() => {
                    loadUncategorised();
                });
            });
            getCustomerTags(() => {
                availableTags.value = (state.customerTags ?? []).map((ct) => ct.tag);
            });
        });
        return (_ctx, _cache) => {
            const _component_v_carousel_item = _resolveComponent("v-carousel-item");
            const _component_v_carousel = _resolveComponent("v-carousel");
            const _component_v_progress_circular = _resolveComponent("v-progress-circular");
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(HeaderBlock, {
                    title: "Uncategorised",
                    logicalBack: true,
                    onLogicalBack: onBack
                }),
                _createElementVNode("div", {
                    class: "tb-swipe-area",
                    onTouchstart: onSwipeStart,
                    onTouchend: onSwipeEnd
                }, [
                    (current.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_1, _toDisplayString(queue.value.length) + " merchant" + _toDisplayString(queue.value.length === 1 ? '' : 's') + " left", 1))
                        : _createCommentVNode("", true),
                    (current.value && _unref(state).bankInfo)
                        ? (_openBlock(), _createBlock(_component_v_carousel, {
                            key: 1,
                            modelValue: index.value,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((index).value = $event)),
                            "show-arrows": false,
                            "hide-delimiter-background": "",
                            "hide-delimiters": "",
                            continuous: false,
                            class: "tb-carousel"
                        }, {
                            default: _withCtx(() => [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(queue.value, (g, i) => {
                                    return (_openBlock(), _createBlock(_component_v_carousel_item, {
                                        key: g.key ?? i
                                    }, {
                                        default: _withCtx(() => [
                                            _createVNode(TransactionGroupCard, {
                                                account: getAccount(g.value[0]),
                                                merchant: g.key,
                                                amount: getGroupAmount(g.value),
                                                date: g.value[0].timestampFormatted ?? formatTimestamp(g.value[0].timestamp),
                                                rawDate: g.value[0].timestamp,
                                                count: g.value.length,
                                                selected: i === index.value,
                                                formatter: _unref(state).formatter,
                                                transactionId: g.value[0].id,
                                                description: g.value[0].description,
                                                hasKnownMerchant: !!g.value[0].merchant
                                            }, null, 8, ["account", "merchant", "amount", "date", "rawDate", "count", "selected", "formatter", "transactionId", "description", "hasKnownMerchant"])
                                        ]),
                                        _: 2
                                    }, 1024));
                                }), 128))
                            ]),
                            _: 1
                        }, 8, ["modelValue"]))
                        : _createCommentVNode("", true),
                    (current.value)
                        ? (_openBlock(), _createBlock(PaginationDots, {
                            key: 2,
                            class: "tb-dots",
                            "total-items": queue.value.length,
                            "current-index": index.value,
                            maxDots: 5
                        }, null, 8, ["total-items", "current-index"]))
                        : _createCommentVNode("", true),
                    (current.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                            (suggestionLoading.value && !suggestion.value)
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                    _cache[11] || (_cache[11] = _createElementVNode("div", { class: "tb-suggest-avatar tb-suggest-avatar--hidden" }, null, -1)),
                                    _createElementVNode("div", _hoisted_3, [
                                        _createTextVNode(" Mark " + _toDisplayString(markLabel.value) + " as ", 1),
                                        _cache[10] || (_cache[10] = _createElementVNode("span", { class: "tb-suggest-skeleton tb-suggest-skeleton--caption" }, null, -1))
                                    ]),
                                    _createElementVNode("a", {
                                        class: "tb-suggest-view",
                                        onClick: _cache[1] || (_cache[1] = ($event) => (txDrawerOpen.value = true))
                                    }, "View " + _toDisplayString(viewLabel.value), 1)
                                ], 64))
                                : (isOtherSuggestion.value)
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                        _cache[12] || (_cache[12] = _createElementVNode("div", { class: "tb-suggest-avatar tb-suggest-avatar--hidden" }, null, -1)),
                                        _cache[13] || (_cache[13] = _createElementVNode("div", { class: "tb-suggest-caption" }, "Choose a category", -1)),
                                        _createElementVNode("a", {
                                            class: "tb-suggest-view",
                                            onClick: _cache[2] || (_cache[2] = ($event) => (txDrawerOpen.value = true))
                                        }, "View " + _toDisplayString(viewLabel.value), 1)
                                    ], 64))
                                    : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                        _createElementVNode("div", {
                                            class: "tb-suggest-avatar",
                                            style: _normalizeStyle(suggestionAvatarStyle.value)
                                        }, [
                                            (suggestionIconUrl.value)
                                                ? (_openBlock(), _createElementBlock("img", {
                                                    key: 0,
                                                    src: suggestionIconUrl.value,
                                                    class: "tb-suggest-avatar-img",
                                                    alt: ""
                                                }, null, 8, _hoisted_4))
                                                : _createCommentVNode("", true)
                                        ], 4),
                                        _createElementVNode("div", _hoisted_5, [
                                            _createTextVNode(" Mark " + _toDisplayString(markLabel.value) + " as ", 1),
                                            _createElementVNode("strong", null, _toDisplayString(suggestionDisplayLower.value) + "?", 1)
                                        ]),
                                        _createElementVNode("a", {
                                            class: "tb-suggest-view",
                                            onClick: _cache[3] || (_cache[3] = ($event) => (txDrawerOpen.value = true))
                                        }, "View " + _toDisplayString(viewLabel.value), 1)
                                    ], 64))
                        ]))
                        : _createCommentVNode("", true)
                ], 32),
                (current.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                        _cache[22] || (_cache[22] = _createElementVNode("div", { class: "tb-actions-handle" }, null, -1)),
                        _createElementVNode("div", _hoisted_7, [
                            _cache[14] || (_cache[14] = _createElementVNode("span", null, "Always apply to merchant", -1)),
                            _createVNode(MGToggle, {
                                modelValue: alwaysApply.value,
                                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((alwaysApply).value = $event)),
                                tick: true
                            }, null, 8, ["modelValue"])
                        ]),
                        _createElementVNode("div", _hoisted_8, [
                            _cache[15] || (_cache[15] = _createElementVNode("span", null, "Exclude from Spending Analysis", -1)),
                            _createVNode(MGToggle, {
                                modelValue: excludeFromAnalysis.value,
                                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((excludeFromAnalysis).value = $event)),
                                tick: true
                            }, null, 8, ["modelValue"])
                        ]),
                        _createElementVNode("div", _hoisted_9, [
                            (!suggestionLoading.value && isOtherSuggestion.value)
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                    _createElementVNode("button", {
                                        class: "tb-cta",
                                        disabled: applying.value,
                                        onClick: onYes
                                    }, [
                                        _createElementVNode("div", _hoisted_11, [
                                            _createElementVNode("img", {
                                                src: require('@/assets/transactions/other.png'),
                                                class: "tb-cta-other-icon",
                                                alt: ""
                                            }, null, 8, _hoisted_12)
                                        ]),
                                        _createElementVNode("div", _hoisted_13, [
                                            _cache[16] || (_cache[16] = _createTextVNode("Assign to")),
                                            _cache[17] || (_cache[17] = _createElementVNode("br", null, null, -1)),
                                            _createTextVNode(_toDisplayString(otherAssignLabel.value), 1)
                                        ])
                                    ], 8, _hoisted_10),
                                    _createElementVNode("button", {
                                        class: "tb-cta",
                                        onClick: onNo
                                    }, _cache[18] || (_cache[18] = [
                                        _createElementVNode("div", { class: "tb-cta-circle tb-cta-circle--choose" }, [
                                            _createElementVNode("span", {
                                                class: "tb-cta-plus",
                                                "aria-hidden": "true"
                                            })
                                        ], -1),
                                        _createElementVNode("div", { class: "tb-cta-label" }, [
                                            _createTextVNode("Choose a"),
                                            _createElementVNode("br"),
                                            _createTextVNode("category")
                                        ], -1)
                                    ]))
                                ], 64))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                    _createElementVNode("button", {
                                        class: "tb-cta",
                                        onClick: onNo
                                    }, _cache[19] || (_cache[19] = [
                                        _createElementVNode("div", { class: "tb-cta-circle tb-cta-circle--choose" }, [
                                            _createElementVNode("span", {
                                                class: "tb-cta-plus",
                                                "aria-hidden": "true"
                                            })
                                        ], -1),
                                        _createElementVNode("div", { class: "tb-cta-label" }, [
                                            _createTextVNode("Choose a"),
                                            _createElementVNode("br"),
                                            _createTextVNode("category")
                                        ], -1)
                                    ])),
                                    _createElementVNode("button", {
                                        class: "tb-cta",
                                        disabled: suggestionLoading.value || !suggestion.value || applying.value,
                                        onClick: onYes
                                    }, [
                                        _createElementVNode("div", {
                                            class: _normalizeClass(["tb-cta-circle tb-cta-circle--assign", { 'tb-cta-circle--loading': suggestionLoading.value || applying.value }])
                                        }, [
                                            (suggestionLoading.value || applying.value)
                                                ? (_openBlock(), _createBlock(_component_v_progress_circular, {
                                                    key: 0,
                                                    indeterminate: "",
                                                    size: "24",
                                                    width: "2",
                                                    color: "white"
                                                }))
                                                : (_openBlock(), _createElementBlock("img", {
                                                    key: 1,
                                                    src: require('@/assets/transactions/tick.png'),
                                                    class: "tb-cta-tick-icon",
                                                    alt: ""
                                                }, null, 8, _hoisted_15))
                                        ], 2),
                                        _createElementVNode("div", _hoisted_16, [
                                            _cache[20] || (_cache[20] = _createTextVNode(" Assign to")),
                                            _cache[21] || (_cache[21] = _createElementVNode("br", null, null, -1)),
                                            (suggestionLoading.value)
                                                ? (_openBlock(), _createElementBlock("span", _hoisted_17))
                                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                    _createTextVNode(_toDisplayString(suggestionDisplayUpper.value), 1)
                                                ], 64))
                                        ])
                                    ], 8, _hoisted_14)
                                ], 64))
                        ])
                    ]))
                    : _createCommentVNode("", true),
                _createVNode(SimpleDrawerPanel, {
                    modelValue: txDrawerOpen.value,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((txDrawerOpen).value = $event)),
                    title: currentMerchantName.value || 'Transactions',
                    height: "90vh"
                }, {
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_18, [
                            (current.value)
                                ? (_openBlock(), _createBlock(TransactionsList, {
                                    key: 0,
                                    search: false,
                                    transactions: groupedCurrentTransactions.value,
                                    formatter: _unref(state).formatter,
                                    today: _unref(state).today,
                                    yesterday: _unref(state).yesterday,
                                    categoryPills: true,
                                    onTransactionClick: (t) => _unref(router).push('/TransactionDetail/' + t.id)
                                }, null, 8, ["transactions", "formatter", "today", "yesterday", "onTransactionClick"]))
                                : _createCommentVNode("", true)
                        ])
                    ]),
                    _: 1
                }, 8, ["modelValue", "title"]),
                _createVNode(ChangeCategoryDrawer, {
                    ref_key: "drawer",
                    ref: drawer,
                    merchantName: currentMerchantName.value,
                    currentClassification: null,
                    currentExcludeFromAnalysis: excludeFromAnalysis.value,
                    selectableTags: availableTags.value ?? [],
                    currentTags: [],
                    onApply: onPickedFromDrawer
                }, null, 8, ["merchantName", "currentExcludeFromAnalysis", "selectableTags"]),
                _createVNode(MGDialog, {
                    modelValue: excludeDialogVisible.value,
                    "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event) => ((excludeDialogVisible).value = $event)),
                    "max-width": "400",
                    "content-height": "auto",
                    title: `${excludeFromAnalysis.value ? 'Exclude' : 'Include'} transaction`,
                    persistent: true
                }, {
                    content: _withCtx(() => [
                        _createElementVNode("p", null, [
                            _createTextVNode("Would you like to always " + _toDisplayString(excludeFromAnalysis.value ? 'exclude' : 'include') + " transactions", 1),
                            (currentMerchantName.value)
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                    _cache[23] || (_cache[23] = _createTextVNode(" from ")),
                                    _createElementVNode("strong", null, _toDisplayString(currentMerchantName.value), 1)
                                ], 64))
                                : _createCommentVNode("", true),
                            _cache[24] || (_cache[24] = _createTextVNode("?"))
                        ])
                    ]),
                    actions: _withCtx(() => [
                        _createVNode(MGButton, {
                            text: `Yes, always ${excludeFromAnalysis.value ? 'exclude' : 'include'}`,
                            fullWidth: true,
                            onClick: _cache[7] || (_cache[7] = ($event) => (confirmAlwaysExcludeInclude(true)))
                        }, null, 8, ["text"]),
                        _createVNode(MGButton, {
                            stacked: true,
                            text: "No, just this once",
                            fullWidth: true,
                            onClick: _cache[8] || (_cache[8] = ($event) => (confirmAlwaysExcludeInclude(false)))
                        }),
                        _createVNode(MGButton, {
                            stacked: true,
                            text: "Back",
                            fullWidth: true,
                            outline: true,
                            onClick: cancelExcludeChange
                        })
                    ]),
                    _: 1
                }, 8, ["modelValue", "title"])
            ], 64));
        };
    }
});
